import { ObjectConfig, required } from "@homebound/form-state";
import { ChangeEventReadyPlanOptionInput, SaveChangeEventInput } from "src/generated/graphqlTypes";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventConfigType } from "src/routes/projects/change-events/guided/utils";
import { GuidedChangeEventPage_ChangeEventFragment } from "../../GuidedChangeEventPage.generated";

export type ApplyConfigurationFormInput = BaseChangeEventFormInput &
  Pick<
    SaveChangeEventInput,
    "readyPlanId" | "originalReadyPlanId" | "originalReadyPlanOptions" | "proposedReadyPlanOptions" | "checkoutConfigId"
  > & {
    // Display-only fields
    /** which configuration type the selection modal shows (POC or CCFG). */
    selectedConfigType?: GuidedChangeEventConfigType | null;
    selectedConfigId?: string | null;
  };

const readyPlanOptionConfig: ObjectConfig<ChangeEventReadyPlanOptionInput> = {
  id: { type: "value" },
  readyPlanOptionId: { type: "value", rules: [required] },
  priceInCents: { type: "value" },
};

// Exported so the partial workflow and the tests build their form state from the same config.
export const applyConfigurationFormConfig: ObjectConfig<ApplyConfigurationFormInput> = {
  ...baseChangeEventFormConfig,
  selectedConfigType: { type: "value", isLocalOnly: true },
  selectedConfigId: { type: "value", isLocalOnly: true },
  readyPlanId: { type: "value", rules: [required] },
  // A structure with no current configuration legitimately has neither of these
  originalReadyPlanId: { type: "value" },
  originalReadyPlanOptions: { type: "list", config: readyPlanOptionConfig },
  checkoutConfigId: { type: "value" },
  proposedReadyPlanOptions: {
    type: "list",
    // The options are what actually gets applied, so an empty list means there is nothing to save
    rules: [({ value }) => (value.nonEmpty ? undefined : "At least one configuration option is required")],
    config: readyPlanOptionConfig,
  },
};

/**
 * Rebuilds the form from a saved change event, so edit mode / a mid-wizard refresh comes back to
 * what was already saved.
 */
export function applyConfigurationMapToForm(
  changeEvent: GuidedChangeEventPage_ChangeEventFragment,
): ApplyConfigurationFormInput {
  const checkoutConfigId = changeEvent.checkoutConfig?.id;
  return {
    id: changeEvent.id,
    workflow: changeEvent.workflow?.code,
    projectStageId: changeEvent.projectStage.id,
    type: changeEvent.type.code,
    title: changeEvent.title,
    internalNote: changeEvent.internalNote,
    reasonId: changeEvent.reason.id,
    hasPreSignedContract: changeEvent.hasPreSignedContract,
    documentId: changeEvent.document?.id,
    documentName: changeEvent.document?.name,
    readyPlanId: changeEvent.readyPlan?.id,
    originalReadyPlanId: changeEvent.originalReadyPlan?.id,
    checkoutConfigId,
    originalReadyPlanOptions: changeEvent.originalReadyPlanOptions.map(toOptionInput),
    proposedReadyPlanOptions: changeEvent.proposedReadyPlanOptions.map(toOptionInput),
    // Only a checkout config is saved by reference, so it's the only selection we can read back
    selectedConfigType: checkoutConfigId ? GuidedChangeEventConfigType.CheckoutConfig : undefined,
    selectedConfigId: checkoutConfigId,
  };
}

function toOptionInput(
  option: Pick<
    GuidedChangeEventPage_ChangeEventFragment["originalReadyPlanOptions"][number],
    "id" | "priceInCents" | "readyPlanOption"
  >,
): ChangeEventReadyPlanOptionInput {
  return { id: option.id, readyPlanOptionId: option.readyPlanOption.id, priceInCents: option.priceInCents };
}
